import { Component, OnInit, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ShiftService } from 'src/app/service/shift/shift.service';
import { IUser } from 'src/app/interface/user';
import { IUserPhone } from 'src/app/interface/user-phone';
import { AlertService } from 'src/app/service/alert/alert.service';
import {
	IonRefresher,
	IonInfiniteScroll,
	ModalController,
	IonItemSliding,
	IonSelect,
} from '@ionic/angular';
import { IShift } from 'src/app/interface/shift';
import { AuthService } from 'src/app/service/auth/auth.service';
import { CrewUpdatedService } from 'src/app/event/crew-updated/crew-updated.service';
import { MessagesModalComponent } from '../messages-modal/messages-modal.component';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';

@Component({
	selector: 'app-shift',
	templateUrl: './shift.component.html',
	styleUrls: ['./shift.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush

})
export class ShiftComponent implements OnInit {

	@ViewChild(IonRefresher)
	public ionRefresher: IonRefresher;
	@ViewChild(IonInfiniteScroll)
	public infiniteScroll: IonInfiniteScroll;
	@ViewChild('phonesSelect')
	public phonesSelect: IonSelect;

	private page: number;
	private searchBy: string;
	public users: Array<IUser>;
	public requesting: boolean;
	public phones: Array<IUserPhone>;
	public selectedPhone: string;
	public userAuthenticated: IUser;
	private nextPage: boolean = true;

	constructor(
		private shiftService: ShiftService,
		private alertService: AlertService,
		private modalController: ModalController,
		private authService: AuthService,
		private crewUpdatedService: CrewUpdatedService,
		private changeDetectorRef: ChangeDetectorRef,
		private analyticsService: AnalyticsService
	) {
		this.userAuthenticated = this.authService.user;
	}

	public get myShift(): IShift {
		return this.authService.user?.shift;
	}

	public ngOnInit() {
		this.analyticsService.trackScreen('/modal/shift');
		this.init(true);
	}

	public init(reset?: boolean) {
		if (reset) {
			this.page = 1;
		}
		if (this.page <= 0 || this.requesting) {
			return;
		}
		this.requesting = true;
		this.changeDetectorRef.detectChanges();
		this.shiftService
			.companyShifts(this.page, this.searchBy)
			.subscribe({
				next: (response) => {
					if (!this.users || reset) {
						this.users = !this.users
							? []
							: this.users.filter((item) => item.selected);
					}
					if (response.next_page_url) {
						this.page = response.current_page + 1;
					} else {
						this.page = 0;
					}
					if (response.data) {
						response.data.forEach((item, index) => {
							if (
								!this.users.find(
									(item2) => item2.id === item.id
								)
							) {
								// Checkbox
								if (
									this.myShift?.leader_shift_id ===
									item.shift?.leader_shift_id
								) {
									item.selected = true;
								}
								// Sorting
								if (
									this.myShift?.leader_shift_id ===
									item.shift?.leader_shift_id
								) {
									item.sort = 0;
								} else if (item.shift) {
									item.sort = item.shift.leader_shift_id;
								} else {
									item.sort = index + 99999;
								}
								this.users.push(item);
							}
						});
						this.users.sort((a, b) => Number(a.selected ?? Infinity) - Number(b.selected ?? Infinity) && a.sort - b.sort);

						const currentUser = this.users.find( (user: IUser) => user.id === this.userAuthenticated.id);
						if(currentUser) {
							this.users = this.users.filter((user: IUser) => user.id !== this.userAuthenticated.id);
							this.users.unshift(currentUser);
						}
						
					}

					this.nextPage = !!response.next_page_url;
				},
				error: (error) => this.alertService.error(error)
			})
			.add(async () => {
				this.requesting = false;
				this.ionRefresher.disabled = false;
				await this.infiniteScroll.complete();
				this.infiniteScroll.disabled = !this.nextPage;
				this.changeDetectorRef.detectChanges();
			});
	}

	public refresh() {
		this.ionRefresher.disabled = true;
		this.init(true);
	}

	public pagination() {
		this.init();
	}

	public search(event) {
		if (event.target.value.trim().length) {
			this.searchBy = event.target.value.trim();
		} else {
			this.searchBy = null;
		}
		this.init(true);
	}

	public dismiss() {
		this.modalController.dismiss();
	}

	public isLeader(shift: IShift) {
		if (!shift) {
			return false;
		}
		return shift.id === shift.leader_shift_id;
	}

	public isMyLeader(shift: IShift): boolean {
		return (
			this.myShift?.operator_id !== shift?.operator_id &&
			this.myShift?.leader_shift_id === shift?.id
		);
	}

	public amILeader(shift: IShift): boolean {
		return (
			this.myShift?.operator_id === shift?.operator_id &&
			this.myShift?.leader_shift_id === shift?.id
		);
	}

	public inAnotherShift(shift: IShift): boolean {
		if (!shift) {
			return false;
		}
		if (!this.myShift) {
			return true;
		}
		return this.myShift?.leader_shift_id !== shift?.leader_shift_id;
	}

	public inProgress(shift: IShift): boolean {
		if (
			!shift ||
			!this.myShift ||
			this.myShift.leader_shift_id !== shift.leader_shift_id
		) {
			return false;
		}
		return shift.deleted_at === null;
	}

	public update() {
		if (this.requesting) {
			return;
		}
		this.requesting = true;
		this.shiftService
			.store({
				operators: this.users.filter((user) => user.selected),
			})
			.subscribe({
				next: (response) => {
					this.crewUpdatedService.local(response.crew);
					this.alertService.toast(response.message);
					this.dismiss();
				},
				error: (error) => this.alertService.error(error)
			})
			.add(() => {
				this.requesting = false;
				this.changeDetectorRef.detectChanges();
			});
	}

	public requestBringIn(itemSliding: IonItemSliding, user: IUser) {
		if (this.requesting) {
			return;
		}
		this.requesting = true;
		itemSliding.close();
		this.shiftService
			.requestBringIn(user.id)
			.subscribe({
				next: (response) => this.alertService.toast(response.message),
				error: (error) => this.alertService.error(error)
			})
			.add(() => {
				this.requesting = false;
				this.changeDetectorRef.detectChanges();
			});
	}

	public isSelectable(user: IUser) {
		return !this.inAnotherShift(user.shift) && !this.isLeader(user.shift)
	}

	public async messageUser(user: IUser) {
		const modal = await this.modalController.create({
			component: MessagesModalComponent,
			componentProps: {
				chat: {
					chatter_id: user.id,
					chatter_type: "App\\Model\\User",
					user_id: this.userAuthenticated.id,
				}
			}
		});
		modal.present();
	}

	public getPhoneNumber(user: IUser): string {
		let phoneNumber = '+';
		if (user?.phone?.length) {
			phoneNumber += user.phone_code + user.phone;
		} else if (user?.phones?.length) {
			phoneNumber += user.phones[0].phone_code + user.phones[0].phone;
		}
		phoneNumber = phoneNumber.replace('++', '+');
		if (phoneNumber.length < 5) {
			phoneNumber = null;
		}
		return phoneNumber;
	}

	public callPhoneNumber(user: IUser) {
		window.location.href = 'tel:' + this.getPhoneNumber(user);
	}
}
